import { Button } from '@tkpod/ui';
import type { DisplayRule } from '@tkpod/shared';

export interface RuleOptionRef {
  key: string;
  displayName: string;
  optionType: string;
  choices: { key: string; label: string }[];
}

export interface RuleAxisRef {
  optionTypeId: string;
  label: string;
  values: { key: string; label: string }[];
}

const CHOICE_TYPES = new Set(['RADIO', 'SELECT', 'SWATCH', 'BUTTON_GROUP', 'CHECKBOX_GROUP']);
const BOOLEAN_TYPES = new Set(['CHECKBOX', 'BOOLEAN']);

const OPERATOR_LABEL: Record<DisplayRule['conditions'][number]['operator'], string> = {
  EQ: 'is',
  NEQ: 'is not',
  IN: 'is any of',
  NOT_IN: 'is none of',
  GTE: 'at least',
  LTE: 'at most',
};

export function ruleSentence(
  rule: DisplayRule,
  options: readonly RuleOptionRef[],
  axes: readonly RuleAxisRef[],
): string {
  const joiner = rule.combinator === 'OR' ? ' or ' : ' and ';
  const action = rule.action === 'HIDE' ? 'Hide this option when' : 'Show this option when';
  const parts = rule.conditions.map((condition) => conditionSentence(condition, options, axes));
  return `${action} ${parts.join(joiner)}.`;
}

export function DisplayRulesEditor({
  rules,
  options,
  axes,
  fieldErrors,
  onChange,
}: {
  rules: DisplayRule[];
  options: readonly RuleOptionRef[];
  axes: readonly RuleAxisRef[];
  fieldErrors: Map<string, string>;
  onChange: (rules: DisplayRule[]) => void;
}) {
  const updateRule = (index: number, patch: Partial<DisplayRule>) => {
    onChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  };
  const updateCondition = (
    ruleIndex: number,
    conditionIndex: number,
    patch: Partial<DisplayRule['conditions'][number]>,
  ) => {
    const rule = rules[ruleIndex];
    if (!rule) return;
    updateRule(ruleIndex, {
      conditions: rule.conditions.map((condition, i) =>
        i === conditionIndex ? { ...condition, ...patch } : condition,
      ),
    });
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-secondary">
        Leave this empty to always show the option. Show when hides it until the condition is true. Hide when hides it only while the condition is true. The first matching rule wins.
      </p>
      {rules.map((rule, index) => (
        <div key={index} className="space-y-3 rounded-md border border-border p-3">
          <p className="text-sm font-medium text-text-primary">{ruleSentence(rule, options, axes)}</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <select
              className="rounded-md border border-border px-3 py-2 text-sm"
              value={rule.action}
              onChange={(event) => updateRule(index, { action: event.target.value as DisplayRule['action'] })}
            >
              <option value="SHOW">Show when</option>
              <option value="HIDE">Hide when</option>
            </select>
            <select
              className="rounded-md border border-border px-3 py-2 text-sm"
              value={rule.combinator}
              onChange={(event) =>
                updateRule(index, { combinator: event.target.value as DisplayRule['combinator'] })
              }
            >
              <option value="AND">All conditions</option>
              <option value="OR">Any condition</option>
            </select>
          </div>
          {rule.conditions.map((condition, conditionIndex) => {
            const prefix = `displayRules.${index}.conditions.${conditionIndex}`;
            const missing = missingSource(condition, options, axes);
            const operators = operatorsFor(condition, options);
            return (
              <div key={conditionIndex} className="space-y-2 rounded-md bg-surface-secondary p-2">
                <div className="grid gap-2 sm:grid-cols-2">
                  <select
                    className="rounded-md border border-border bg-surface px-2 py-2 text-sm"
                    value={sourceValue(condition)}
                    onChange={(event) => {
                      const next = parseSource(event.target.value, options, axes);
                      updateCondition(index, conditionIndex, next);
                    }}
                  >
                    <optgroup label="Other options">
                      {options.map((option) => (
                        <option key={option.key} value={`option:${option.key}`}>
                          {option.displayName || option.key}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Variant axes">
                      {axes.map((axis) => (
                        <option key={axis.optionTypeId} value={`axis:${axis.optionTypeId}`}>
                          {axis.label}
                        </option>
                      ))}
                    </optgroup>
                    <option value="quantity">Quantity</option>
                  </select>
                  <select
                    className="rounded-md border border-border bg-surface px-2 py-2 text-sm"
                    value={condition.operator}
                    onChange={(event) =>
                      updateCondition(index, conditionIndex, {
                        operator: event.target.value as DisplayRule['conditions'][number]['operator'],
                      })
                    }
                  >
                    {operators.map((operator) => (
                      <option key={operator} value={operator}>
                        {OPERATOR_LABEL[operator]}
                      </option>
                    ))}
                  </select>
                </div>
                <ValueControl
                  condition={condition}
                  options={options}
                  axes={axes}
                  onChange={(values) => updateCondition(index, conditionIndex, { values })}
                />
                {missing ? <p className="text-xs text-danger-700">{missing}</p> : null}
                {fieldErrors.get(`${prefix}.sourceKey`) ? (
                  <p className="text-xs text-danger-700">{fieldErrors.get(`${prefix}.sourceKey`)}</p>
                ) : null}
                {fieldErrors.get(`${prefix}.values`) ? (
                  <p className="text-xs text-danger-700">{fieldErrors.get(`${prefix}.values`)}</p>
                ) : null}
                {fieldErrors.get(`${prefix}.operator`) ? (
                  <p className="text-xs text-danger-700">{fieldErrors.get(`${prefix}.operator`)}</p>
                ) : null}
                {rule.conditions.length > 1 ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      updateRule(index, {
                        conditions: rule.conditions.filter((_, i) => i !== conditionIndex),
                      })
                    }
                  >
                    Remove condition
                  </Button>
                ) : null}
              </div>
            );
          })}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                updateRule(index, {
                  conditions: [...rule.conditions, blankCondition(options, axes)],
                })
              }
            >
              Add condition
            </Button>
            <Button size="sm" variant="outline" onClick={() => onChange(rules.filter((_, i) => i !== index))}>
              Remove rule
            </Button>
          </div>
        </div>
      ))}
      <Button
        size="sm"
        variant="outline"
        onClick={() =>
          onChange([
            ...rules,
            { action: 'SHOW', combinator: 'AND', conditions: [blankCondition(options, axes)] },
          ])
        }
      >
        Add rule
      </Button>
    </div>
  );
}

function ValueControl({
  condition,
  options,
  axes,
  onChange,
}: {
  condition: DisplayRule['conditions'][number];
  options: readonly RuleOptionRef[];
  axes: readonly RuleAxisRef[];
  onChange: (values: string[]) => void;
}) {
  if (condition.source === 'QUANTITY') {
    return (
      <input
        type="number"
        min={1}
        className="w-full rounded-md border border-border bg-surface px-2 py-2 text-sm"
        value={condition.values[0] ?? ''}
        onChange={(event) => onChange(event.target.value.trim() === '' ? [] : [event.target.value])}
      />
    );
  }
  const option = condition.source === 'OPTION' ? options.find((item) => item.key === condition.sourceKey) : undefined;
  if (option && BOOLEAN_TYPES.has(option.optionType)) {
    return (
      <select
        className="w-full rounded-md border border-border bg-surface px-2 py-2 text-sm"
        value={condition.values[0] ?? 'true'}
        onChange={(event) => onChange([event.target.value])}
      >
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  const choices =
    condition.source === 'VARIANT_AXIS'
      ? (axes.find((axis) => axis.optionTypeId === condition.sourceKey)?.values ?? [])
      : option && CHOICE_TYPES.has(option.optionType)
        ? option.choices
        : null;
  if (choices) {
    const selected = new Set(condition.values);
    return (
      <div className="flex flex-wrap gap-2">
        {choices.length === 0 ? <p className="text-xs text-text-secondary">This source has no choices yet.</p> : null}
        {choices.map((choice) => (
          <label key={choice.key} className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={selected.has(choice.key)}
              onChange={(event) => {
                const next = new Set(selected);
                if (event.target.checked) next.add(choice.key);
                else next.delete(choice.key);
                onChange([...next]);
              }}
            />
            {choice.label}
          </label>
        ))}
      </div>
    );
  }
  return (
    <input
      className="w-full rounded-md border border-border bg-surface px-2 py-2 text-sm"
      placeholder="Value"
      value={condition.values[0] ?? ''}
      onChange={(event) => onChange(event.target.value.trim() === '' ? [] : [event.target.value])}
    />
  );
}

function operatorsFor(
  condition: DisplayRule['conditions'][number],
  options: readonly RuleOptionRef[],
): DisplayRule['conditions'][number]['operator'][] {
  if (condition.source === 'QUANTITY') return ['GTE', 'LTE'];
  const option = options.find((item) => item.key === condition.sourceKey);
  if (option && BOOLEAN_TYPES.has(option.optionType)) return ['EQ', 'NEQ'];
  return ['EQ', 'NEQ', 'IN', 'NOT_IN'];
}

function sourceValue(condition: DisplayRule['conditions'][number]): string {
  if (condition.source === 'QUANTITY') return 'quantity';
  if (condition.source === 'VARIANT_AXIS') return `axis:${condition.sourceKey}`;
  return `option:${condition.sourceKey}`;
}

function parseSource(
  value: string,
  options: readonly RuleOptionRef[],
  axes: readonly RuleAxisRef[],
): DisplayRule['conditions'][number] {
  if (value === 'quantity') {
    return { source: 'QUANTITY', sourceKey: 'quantity', operator: 'GTE', values: ['1'] };
  }
  if (value.startsWith('axis:')) {
    const sourceKey = value.slice(5);
    const axis = axes.find((item) => item.optionTypeId === sourceKey);
    return {
      source: 'VARIANT_AXIS',
      sourceKey,
      operator: 'EQ',
      values: axis?.values[0] ? [axis.values[0].key] : [],
    };
  }
  const sourceKey = value.startsWith('option:') ? value.slice(7) : value;
  const option = options.find((item) => item.key === sourceKey);
  return {
    source: 'OPTION',
    sourceKey,
    operator: 'EQ',
    values: option && CHOICE_TYPES.has(option.optionType) && option.choices[0] ? [option.choices[0].key] : [],
  };
}

function blankCondition(
  options: readonly RuleOptionRef[],
  axes: readonly RuleAxisRef[],
): DisplayRule['conditions'][number] {
  const option = options[0];
  if (option) return parseSource(`option:${option.key}`, options, axes);
  const axis = axes[0];
  if (axis) return parseSource(`axis:${axis.optionTypeId}`, options, axes);
  return parseSource('quantity', options, axes);
}

function missingSource(
  condition: DisplayRule['conditions'][number],
  options: readonly RuleOptionRef[],
  axes: readonly RuleAxisRef[],
): string | null {
  if (condition.source === 'OPTION' && !options.some((option) => option.key === condition.sourceKey)) {
    return 'Choose an option that still exists.';
  }
  if (condition.source === 'VARIANT_AXIS' && !axes.some((axis) => axis.optionTypeId === condition.sourceKey)) {
    return 'Choose an axis that still exists.';
  }
  return null;
}

function conditionSentence(
  condition: DisplayRule['conditions'][number],
  options: readonly RuleOptionRef[],
  axes: readonly RuleAxisRef[],
): string {
  const verb = OPERATOR_LABEL[condition.operator];
  if (condition.source === 'QUANTITY') {
    return `Quantity is ${verb} ${condition.values[0] ?? '?'}`;
  }
  if (condition.source === 'VARIANT_AXIS') {
    const axis = axes.find((item) => item.optionTypeId === condition.sourceKey);
    const labels = labelsFor(condition.values, axis?.values ?? []);
    return `${axis?.label ?? 'Axis'} ${verb} ${labels}`;
  }
  const option = options.find((item) => item.key === condition.sourceKey);
  const name = option?.displayName || condition.sourceKey || 'Option';
  if (option && BOOLEAN_TYPES.has(option.optionType)) {
    return `${name} ${verb} ${condition.values[0] === 'false' ? 'No' : 'Yes'}`;
  }
  const labels = option && CHOICE_TYPES.has(option.optionType)
    ? labelsFor(condition.values, option.choices)
    : condition.values.join(', ') || '?';
  return `${name} ${verb} ${labels}`;
}

function labelsFor(keys: readonly string[], choices: readonly { key: string; label: string }[]): string {
  if (keys.length === 0) return '?';
  return keys
    .map((key) => choices.find((choice) => choice.key === key)?.label ?? key)
    .join(', ');
}
